/**
 * Mobile Drawer Authentication Hook
 *
 * Manages authentication-related functionality for the mobile drawer
 * including user data, permissions, and sign-out handling.
 */

import { useCallback } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useSignOut } from "@/lib/utils/auth/sign-out";
import { useFeatureFlags } from "@/lib/contexts/feature-flags-context";
import { useCRMRoleCheck } from "@/lib/hooks/auth/useRoleCheck";

interface UseMobileDrawerAuthProps {
  onOpenChange: (open: boolean) => void;
}

export function useMobileDrawerAuth({ onOpenChange }: UseMobileDrawerAuthProps) {
  const { appUser, isClientUser, isEmployee, isAdmin } = useUniversalAuth();
  const { signOut } = useSignOut();
  const { isFeatureEnabled } = useFeatureFlags();
  const { canAccessCRM } = useCRMRoleCheck();

  const getUserTypeDisplay = useCallback(() => {
    if (isAdmin) return "Admin";
    if (isEmployee) return "Employee";
    if (isClientUser) return "Client";
    return "User";
  }, [isAdmin, isEmployee, isClientUser]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    onOpenChange(false);
  }, [signOut, onOpenChange]);

  return {
    appUser,
    isClientUser,
    isEmployee,
    isAdmin,
    canAccessCRM,
    isFeatureEnabled,
    getUserTypeDisplay,
    handleSignOut,
  };
}
